import { Button, Field, Modal } from "@deligoo/ui"
import dayjs from "dayjs"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"

import { useAppSelector } from "@/App"
import { selectDriverById, updateDriver } from "@/store/drivers"
import { Driver } from "@/types"
import { fetchSetNewBreak } from "@/utils"
import { handleStandardError } from "@/utils/errors"

import cls from "../DriverContextMenu.module.scss"

type DriversBreakModalProps = {
  driverId: Driver["id"]
  handleClose: () => void
}

export const DriversBreakModal = ({ driverId, handleClose }: DriversBreakModalProps) => {
  const driver = useAppSelector(selectDriverById(driverId))

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [breakDuration, setBreakDuration] = useState(0)
  const [breakStartsAt, setBreakStartsAt] = useState(dayjs().format("HH:mm"))
  const [breakReason, setBreakReason] = useState("")

  const dispatch = useDispatch()

  const { t } = useTranslation()

  if (!driver) return null

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const breakStartTime = dayjs(breakStartsAt, "HH:mm").format()

    if (!driver) return null
    setIsSubmitting(true)

    try {
      const { response, json } = await fetchSetNewBreak(driver.id, breakStartTime, breakDuration, breakReason)
      const updatedDriver = json?.data

      if (response.ok && updatedDriver) {
        toast.success(t("drivers:new_break_success"))
        dispatch(updateDriver(updatedDriver))
        handleClose()
      } else {
        handleStandardError(response, json)
      }
    } catch (error) {
      toast.error(t("drivers:new_break_fail"))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal handleClose={handleClose} className={cls.driversBreakModal} closeBtnPosition="inside">
      <Modal.Title>
        {t("drivers:courier")}: {driver.name}
        <div className="h300 mt-4">{t("drivers:add_new_break")}</div>
      </Modal.Title>

      <Modal.Content>
        <form onSubmit={handleSubmit}>
          <Field
            className="mt-2 mb-2"
            label={t("drivers:new_break_start")}
            name="start_time"
            inputProps={{
              value: breakStartsAt,
              onChange: (e) => setBreakStartsAt(e.currentTarget.value),
              type: "time",
              required: true,
              min: dayjs().format("HH:mm"),
            }}
          />

          <Field
            className="mt-6 mb-2"
            label={t("drivers:new_break_duration")}
            name="end_time"
            inputProps={{
              value: breakDuration,
              onChange: (e) => setBreakDuration(parseInt(e.currentTarget.value)),
              type: "number",
              required: true,
              autoFocus: true,
              min: 1,
            }}
          />

          <Field
            label="Powód"
            name="reason"
            className="mt-5"
            inputProps={{
              value: breakReason,
              required: true,
              onChange: (e) => setBreakReason(e.currentTarget.value),
            }}
          />

          <div className={cls.actions}>
            <Button onClick={handleClose} color="muted" variation="outlined">
              {t("cancel")}
            </Button>

            <Button color="primary" type="submit" raised disabled={isSubmitting}>
              {t("confirm")}
            </Button>
          </div>
        </form>
      </Modal.Content>
    </Modal>
  )
}
